import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import CycleService from '../../services/cycle.service';

const CycleList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCycles();
  }, []);

  const fetchCycles = async () => {
    setLoading(true);
    try {
      const response = await CycleService.getAll();
      setCycles(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching cycles:', error);
      showSnackbar(t('cycles.errorLoadingCycles') + ': ' + (error.response?.data?.message || error.message), 'error');
      setCycles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await CycleService.delete(deleteDialog.id);
      showSnackbar(t('cycles.cycleDeletedSuccess'));
      fetchCycles();
    } catch (error) {
      showSnackbar(t('cycles.errorDeletingCycle'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredCycles = cycles.filter(cycle =>
    cycle.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('cycles.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('cycles.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/cycles/deleted')}
                >
                  {t('cycles.deletedCycles')}
                </Button>
              )}
              {hasPermission('cycles.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/cycles/new')}
                >
                  {t('cycles.newCycle')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('cycles.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('cycles.name')}</TableCell>
                  <TableCell>{t('cycles.createdAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCycles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('cycles.noCyclesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCycles.map((cycle) => (
                    <TableRow key={cycle.id} hover>
                      <TableCell>{cycle.name}</TableCell>
                      <TableCell>{formatDate(cycle.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('cycles.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/cycles/${cycle.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('cycles.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(cycle.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('cycles.deleteCycleTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('cycles.deleteCycleConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CycleList;
